import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from "@angular/core";
import { WizardSubstep } from "../../../common/wizard/wizard-subtep.interface";
import { UserService } from "../../../../domain/user-service";
import { Font } from "../../../../domain/font";
import { AspectRatio } from "../../../../domain/enums";
import { Category } from "../../../../domain/category";
import {
  AudioLangSettings,
  ChannelInfo,
  ChannelSignalCommandSetting,
  ClosedCaptionSetting,
  EAutoExtendLiveShowBehaviour,
  EClosedCaptionsTransport,
  EClosedCaptionsType,
  SecondaryEventSignalStandardType,
  UploadPlaylistCsvSettings,
} from "../../../../domain/channel";
import { Tag } from "../../../../domain/tag";
import { TagRepository } from "../../../../domain/tag-repository";
import { ChannelRepository } from "../../../../domain/channel-repository";
import { ToastrService } from "ngx-toastr";
import { CdkDragDrop, moveItemInArray } from "@angular/cdk/drag-drop";

const CUE_P_ON_COMMAND_NAME = "CUE_P_ON";

@Component({
  selector: "icn-channel-wizard-step1-substep1",
  templateUrl: "./substep1.html",
  styleUrls: ["./substep1.scss"],
})
export class ChannelWizardStep1Substep1Component
  implements WizardSubstep, OnInit
{
  @Input() channelInfo: ChannelInfo;
  @Input() name = "";
  @Input() description = "";
  @Input() timezoneId: number;
  @Input() burnSubtitlesToVideo: boolean;
  @Input() subtitleLanguage: string;
  @Input() subtitlesSettings: {
    fontId: number;
    size: number;
    subtitleStartPositionFromTop: number;
    subtitleEndPositionFromTop: number;
  };
  @Input() framerate: number;
  @Input() audioNormalizationChecked: boolean;
  @Input() cuePeriodChecked = false;
  @Input() playSingleItemPerDepth: boolean;
  @Input() lufsChecked: boolean;
  @Input() audioNormalizationValue: number;
  @Input() aspectRatio: AspectRatio;
  @Input() channelAsTemplateChecked: boolean;
  @Input() uploadPlaylistCsvSettings: UploadPlaylistCsvSettings;
  @Input() channelIsRecordingActiveChecked: boolean;
  @Input() allCategories: Array<Category>;
  @Input() selectedChannelCategories: Array<number>;
  @Input() templateTrailerUrl: string;
  @Input() mainLanguageCode: string;
  @Input() audioLangSettings: AudioLangSettings[] = [];
  @Input() playbackOffset: number;
  @Input() autoExtendLiveShowBehaviour: EAutoExtendLiveShowBehaviour;
  @Input() closedCaptions: ClosedCaptionSetting[] = [];

  @Output() playSingleItemPerDepthChange = new EventEmitter<boolean>();
  @Output() nameChange = new EventEmitter<string>();
  @Output() descriptionChange = new EventEmitter<string>();
  @Output() timezoneIdChange = new EventEmitter<number>();
  @Output() subtitleLanguageChange = new EventEmitter<string>();
  @Output() onMainLanguageChange = new EventEmitter<string>();
  @Output() subtitlesSettingsChange = new EventEmitter();
  @Output() framerateChange = new EventEmitter<number>();
  @Output() audioNormalizationCheckedChange = new EventEmitter<boolean>();
  @Output() useLufsCheckedChange = new EventEmitter<boolean>();
  @Output() audioNormalizationValueChange = new EventEmitter<number>();
  @Output() burnSubtitlesToVideoChange = new EventEmitter<boolean>();
  @Output() aspectRatioChange = new EventEmitter<AspectRatio>();
  @Output() availabilityZoneIdsChange = new EventEmitter<number[]>();
  @Output() channelAsTemplatedChanged = new EventEmitter<boolean>();

  @Output() channelIsRecordingChanged = new EventEmitter<boolean>();
  @Output() templateTrailerUrlChanged = new EventEmitter<string>();
  @Output() playbackOffsetChanged = new EventEmitter<number>();
  @Output() audioLangSettingsChange = new EventEmitter<AudioLangSettings[]>();
  @Output() closedCaptionsChange = new EventEmitter<ClosedCaptionSetting[]>();

  placeholderChannelName = "Channel name";
  placeholderChannelDescription = "Channel description";
  fallbackPickerValues: { [index: number]: string } = {};
  openFallbackPopupIndex: number | null = null;
  EAutoExtendLiveShowBehaviour = EAutoExtendLiveShowBehaviour;
  closeIcon = "/assets/icons/x-close-pop.svg";
  editingClosedCaptionLang: string = null;
  captionsTransportOptions: { name: string; value: EClosedCaptionsTransport }[] =
    [
      { name: "VBI", value: EClosedCaptionsTransport.VBI },
      { name: "DTVCC", value: EClosedCaptionsTransport.DTVCC },
    ];
  captionsTypeOptions: { name: string; value: EClosedCaptionsType }[] = [
    { name: "Line 21 CC1", value: EClosedCaptionsType.Line21CC1 },
    { name: "Line 21 CC2", value: EClosedCaptionsType.Line21CC2 },
    { name: "Line 21 CC3", value: EClosedCaptionsType.Line21CC3 },
    { name: "Line 21 CC4", value: EClosedCaptionsType.Line21CC4 },
    { name: "Line 21 T1", value: EClosedCaptionsType.Line21T1 },
    { name: "Line 21 T2", value: EClosedCaptionsType.Line21T2 },
    { name: "Line 21 T3", value: EClosedCaptionsType.Line21T3 },
    { name: "Line 21 T4", value: EClosedCaptionsType.Line21T4 },
    { name: "Line 21 XDS", value: EClosedCaptionsType.Line21XDS },
    { name: "DTVCC", value: EClosedCaptionsType.DTVCC },
  ];
  languages: { code: string; name: string }[];
  fonts: Font[];
  timezones: { id: number; name: string; value: string }[];

  isSuperUser = false;
  channelTimezone: string;

  allowedHoursFromNowSelected = false;

  tags: Tag[] = [];
  availableTags = [];

  availableScteCommands: ChannelSignalCommandSetting[];
  @Output() cuePeriodCheckedChange = new EventEmitter<boolean>();
  @Output() autoExtendLiveShowBehaviourChanged: EventEmitter<any> =
    new EventEmitter();

  constructor(
    private userService: UserService,
    private tagRepository: TagRepository,

    private _channelRepository: ChannelRepository,
    private _toastr: ToastrService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {
    const user = userService.getCurrentUser();
    this.isSuperUser = user.isSuperUser;
    this.timezones = user.timezones;
    this.languages = user.languages;
    this.fonts = user.fonts;
  }

  ngOnInit(): void {
    this.availableScteCommands = [
      ...this.channelInfo.signalCommands.filter(
        (x) => x.standartType === SecondaryEventSignalStandardType.CuePeriod,
      ),
    ];
    this.cuePeriodChecked = this.hasCuePeriodOnCommand();
    this.cuePeriodCheckedChange.emit(this.cuePeriodChecked);
    this.availableTags = this.tagRepository.getGeneralTags();
    this.tags = this.availableTags.filter((t) =>
      this.channelInfo.scteTagIds.includes(t.id),
    );

    if (this.uploadPlaylistCsvSettings.allowedHoursFromNow) {
      this.allowedHoursFromNowSelected = true;
    }
    this.changeDetectorRef.detectChanges();
  }

  onOpenSettingsIcon(values, settingsPopover) {
    settingsPopover.show(values);
    this.changeDetectorRef.markForCheck();
  }

  onBurnSubtitlesToVideoChanged() {
    this.burnSubtitlesToVideo = !this.burnSubtitlesToVideo;
    this.burnSubtitlesToVideoChange.emit(this.burnSubtitlesToVideo);
  }

  onSubtitleLanguageChange(subtitle) {
    this.subtitleLanguageChange.emit(subtitle);
  }

  onSubtitleSettingsSave(
    popover,
    fontId: number,
    subtitleStartPositionFromTop: number,
    subtitleEndPositionFromTop: number,
  ) {
    popover.hide();
    this.subtitlesSettings = {
      fontId: fontId,
      size: 12,
      subtitleStartPositionFromTop: subtitleStartPositionFromTop,
      subtitleEndPositionFromTop: subtitleEndPositionFromTop,
    };
    this.subtitlesSettingsChange.emit(this.subtitlesSettings);
  }

  get selectedClosedCaptionLanguages(): string[] {
    return (this.closedCaptions || []).map((c) => c.languageCode);
  }

  getEditingClosedCaption(): ClosedCaptionSetting {
    return (this.closedCaptions || []).find(
      (c) => c.languageCode === this.editingClosedCaptionLang,
    );
  }

  onClosedCaptionLanguagesChange(codes: string[]) {
    const current = this.closedCaptions || [];
    const next = codes.map((code) => {
      const existing = current.find((c) => c.languageCode === code);
      return (
        existing ||
        new ClosedCaptionSetting(
          code,
          this.captionsTransportOptions[0].value,
          this.captionsTypeOptions[0].value,
          1,
        )
      );
    });
    this.closedCaptions = next;
    this.closedCaptionsChange.emit(this.closedCaptions);
  }

  onOpenClosedCaptionSettings(values, popover) {
    this.editingClosedCaptionLang = values?.item?.value;
    popover.show(values?.event);
    this.changeDetectorRef.markForCheck();
  }

  onClosedCaptionSettingsSave(
    popover,
    captionsTransport: EClosedCaptionsTransport,
    captionsType: EClosedCaptionsType,
    captionsServiceNumber: number,
  ) {
    popover.hide();
    const entry = this.getEditingClosedCaption();
    if (!entry) {
      return;
    }
    entry.captionsTransport = captionsTransport;
    entry.captionsType = captionsType;
    entry.captionsServiceNumber = captionsServiceNumber;
    this.closedCaptions = [...this.closedCaptions];
    this.closedCaptionsChange.emit(this.closedCaptions);
  }

  onNameChange(name) {
    this.nameChange.emit(name);
  }

  onDescriptionChange(description) {
    this.descriptionChange.emit(description);
  }

  onTimezoneIdChange(timezoneId) {
    this.timezoneIdChange.emit(timezoneId);
    this.timezoneId = timezoneId;
  }

  canGoNext() {
    return this.validateChannelName();
  }

  msOrFpsChange(value: "ms" | "fps") {
    if (value === "ms") {
      this.framerate = undefined;
    } else {
      this.framerate = 25;
    }
    this.framerateChange.emit(this.framerate);
  }

  onFramerateChange(value) {
    this.framerateChange.emit(value);
  }

  toggleAudioNormalization() {
    this.audioNormalizationChecked = !this.audioNormalizationChecked;
    this.audioNormalizationCheckedChange.emit(this.audioNormalizationChecked);

    if (this.audioNormalizationChecked) {
      this.onAudioNormalizationValueChange(0);
    } else {
      this.onAudioNormalizationValueChange(null);
    }

    this.setUseLufs(false);
  }

  toggleLUFS() {
    this.setUseLufs(!this.lufsChecked);
  }

  setUseLufs(val: boolean) {
    this.lufsChecked = val;
    this.useLufsCheckedChange.emit(this.lufsChecked);
  }

  onAudioNormalizationValueChange(value) {
    this.audioNormalizationValue = value;
    this.audioNormalizationValueChange.emit(value);
  }

  onAspectRatioChange(value) {
    this.aspectRatio = value;
    this.aspectRatioChange.emit(value);
  }

  toggleAsTemplatedChanged() {
    this.channelAsTemplatedChanged.emit(!this.channelAsTemplateChecked);
  }
  toggleIsRecordingActiveChanged() {
    this.channelIsRecordingChanged.emit(!this.channelIsRecordingActiveChecked);
  }
  getTimezones() {
    return this.timezones.sort((a, b) => a.value.localeCompare(b.value));
  }

  validateChannelName() {
    return (
      this.name && this.name.length > 0 && /[^\w-]/gi.test(this.name) === false
    );
  }

  extractMediaIds(mediaIdPrefixesToAlert: Array<string>): string {
    return mediaIdPrefixesToAlert.join(",");
  }

  onMediaIdsFieldChange($event: Event) {
    const val = ($event.target as any).value as string;
    try {
      const prefixesArray = val
        .split(",")
        .filter(
          (item, i, ar) => item.trim().length > 0 && ar.indexOf(item) === i,
        );
      this.uploadPlaylistCsvSettings.mediaIdPrefixesToAlert = [];
      this.changeDetectorRef.markForCheck();
      this.changeDetectorRef.detectChanges();
      this.uploadPlaylistCsvSettings.mediaIdPrefixesToAlert = prefixesArray;
    } catch (e) {}
  }

  allowedHoursFromNowChecked() {
    this.allowedHoursFromNowSelected = !this.allowedHoursFromNowSelected;

    if (this.allowedHoursFromNowSelected) {
      this.uploadPlaylistCsvSettings.allowedHoursFromNow = 0;
    } else {
      this.uploadPlaylistCsvSettings.allowedHoursFromNow = null;
    }
  }

  hasCuePeriodOnCommand(): boolean {
    return this.channelInfo.signalCommands.some(
      (x) => x.commandName === CUE_P_ON_COMMAND_NAME,
    );
  }

  toggleCuePeriod() {
    if (this.hasCuePeriodOnCommand()) {
      return;
    }
    this.cuePeriodChecked = !this.cuePeriodChecked;
    this.cuePeriodCheckedChange.emit(this.cuePeriodChecked);
  }

  playSingleItemPerDepthChangedEventHandler() {
    console.log("playSingleItemPerDepthChangedEventHandler");
    this.playSingleItemPerDepth = !this.playSingleItemPerDepth;
    this.playSingleItemPerDepthChange.emit(this.playSingleItemPerDepth);
    this.changeDetectorRef.markForCheck();
    this.changeDetectorRef.detectChanges();
  }

  addNewTag(tag) {
    if (this.tags.indexOf(tag) < 0) {
      this.tags.push(tag);
      this.channelInfo.scteTagIds.push(tag.id);
    }
    this.changeDetectorRef.markForCheck();
    this.changeDetectorRef.detectChanges();
  }

  deleteExistTag(tag) {
    this.tags.forEach(
      ((savedTag, index) => {
        if (savedTag.id === tag.id) {
          this.tags.splice(index, 1);
        }
      }).bind(this),
    );

    const tagIndex = this.channelInfo.scteTagIds.indexOf(tag.id);
    this.channelInfo.scteTagIds.splice(tagIndex, 1);
  }

  createNewTag(tag) {
    if (tag.trim() === "") {
      return;
    }
    this.tagRepository
      .CreateGeneralTag(tag)
      .then(
        ((response) => {
          /* Updating UI */

          /* Updating Tags */
          if (this.tags.findIndex((tag) => tag.id === response.id) === -1) {
            this.tags.push(response);
            this.channelInfo.scteTagIds.push(response.id);
          }

          this.changeDetectorRef.markForCheck();
          this.changeDetectorRef.detectChanges();
        }).bind(this),
        ((error) => {}).bind(this),
      )
      .catch(((error) => {}).bind(this));
  }
  updateAutoExtendLiveShowBehaviour(value: number) {
    this.autoExtendLiveShowBehaviour = value;
    this.autoExtendLiveShowBehaviourChanged.emit(value);
    this.changeDetectorRef.markForCheck();
    this.changeDetectorRef.detectChanges();
  }

  addAudioLangSetting() {
    this.audioLangSettings = [
      ...(this.audioLangSettings || []),
      new AudioLangSettings("", false, []),
    ];
    this.audioLangSettingsChange.emit(this.audioLangSettings);
  }

  removeAudioLangSetting(index: number) {
    const next = [...(this.audioLangSettings || [])];
    next.splice(index, 1);
    this.audioLangSettings = next;
    this.audioLangSettingsChange.emit(this.audioLangSettings);
  }

  onEntryLanguageCodeChange(index: number, code: string) {
    const next = [...(this.audioLangSettings || [])];
    const entry = next[index];
    if (!entry) {
      return;
    }
    const existingFallbacks = entry.audioFallbackLanguages || [];
    const fallbacks =
      code && !existingFallbacks.includes(code)
        ? [...existingFallbacks, code]
        : existingFallbacks;
    next[index] = new AudioLangSettings(
      code,
      entry.skipOriginalMediaAudioLanguage,
      fallbacks,
    );
    this.audioLangSettings = next;
    this.audioLangSettingsChange.emit(this.audioLangSettings);
  }

  onEntrySkipOriginalChange(index: number, val: boolean) {
    const next = [...(this.audioLangSettings || [])];
    const entry = next[index];
    if (!entry) {
      return;
    }
    next[index] = new AudioLangSettings(
      entry.languageCode,
      val,
      entry.audioFallbackLanguages,
    );
    this.audioLangSettings = next;
    this.audioLangSettingsChange.emit(this.audioLangSettings);
  }

  addFallbackLanguage(index: number, code: string) {
    if (!code) {
      return;
    }
    const next = [...(this.audioLangSettings || [])];
    const entry = next[index];
    if (!entry) {
      return;
    }
    const existing = entry.audioFallbackLanguages || [];
    if (existing.includes(code)) {
      this.fallbackPickerValues[index] = "";
      return;
    }
    next[index] = new AudioLangSettings(
      entry.languageCode,
      entry.skipOriginalMediaAudioLanguage,
      [...existing, code],
    );
    this.audioLangSettings = next;
    this.audioLangSettingsChange.emit(this.audioLangSettings);
    this.fallbackPickerValues[index] = "";
  }

  removeFallbackLanguage(index: number, code: string) {
    const next = [...(this.audioLangSettings || [])];
    const entry = next[index];
    if (!entry) {
      return;
    }
    next[index] = new AudioLangSettings(
      entry.languageCode,
      entry.skipOriginalMediaAudioLanguage,
      (entry.audioFallbackLanguages || []).filter((c) => c !== code),
    );
    this.audioLangSettings = next;
    this.audioLangSettingsChange.emit(this.audioLangSettings);
  }

  onFallbackLanguageDrop(index: number, event: CdkDragDrop<string[]>) {
    const next = [...(this.audioLangSettings || [])];
    const entry = next[index];
    if (!entry) {
      return;
    }
    const reordered = [...(entry.audioFallbackLanguages || [])];
    moveItemInArray(reordered, event.previousIndex, event.currentIndex);
    next[index] = new AudioLangSettings(
      entry.languageCode,
      entry.skipOriginalMediaAudioLanguage,
      reordered,
    );
    this.audioLangSettings = next;
    this.audioLangSettingsChange.emit(this.audioLangSettings);
  }

  getLanguageName(code: string): string {
    const match = (this.languages || []).find((l) => l.code === code);
    return match ? match.name : code;
  }

  trackEntryByIndex(index: number) {
    return index;
  }

  toggleFallbackPopup(index: number) {
    this.openFallbackPopupIndex =
      this.openFallbackPopupIndex === index ? null : index;
  }

  closeFallbackPopup() {
    this.openFallbackPopupIndex = null;
  }

  getAvailableLanguagesFor(index: number) {
    const usedByOthers = new Set(
      (this.audioLangSettings || [])
        .filter((_, i) => i !== index)
        .map((s) => s.languageCode)
        .filter((c) => !!c),
    );
    return this.languages.filter((lang) => !usedByOthers.has(lang.code));
  }

  getAvailableFallbackLanguagesFor(index: number) {
    const entry = (this.audioLangSettings || [])[index];
    const selected = new Set(entry ? entry.audioFallbackLanguages || [] : []);
    return this.languages.filter((lang) => !selected.has(lang.code));
  }
}
